/**
 * I registri di numerazione dell'organizzazione (CF08) — CL05.
 *
 * L'elenco dei registri e le azioni amministrative su ciascuno: azzerare,
 * spostare il cursore, leggerne lo storico.
 *
 * ## Dove sta
 *
 * Il piano vuole questo pannello **accanto al calendario**, nelle impostazioni
 * dell'organizzazione: e' li' che e' montato (`OrganizationDialog`, tab
 * `sequences` dopo priorita', etichette e calendario), con l'identita'
 * dell'azienda.
 *
 * I progressivi **locali** di un modello non si amministrano da qui: nascono e
 * si correggono dal configuratore del campo `autoincrement` che li usa
 * (`SequenceConfiguration`), che e' l'unico posto in cui servono. Uno condiviso
 * invece appartiene a tutti, e spegnerlo dal modello che lo usa sarebbe un
 * effetto che va oltre quel modello.
 *
 * Il commento che stava qui diceva che la superficie calendario non esisteva e
 * che il pannello non era montato da nessuna parte. Era falso: la superficie e'
 * in `src/components/organization-calendar/`, e la ricerca di allora guardava
 * solo `src/pages` e `src/Component`.
 *
 * ## Cosa non fa
 *
 * Non mostra un registro come "il prossimo numero sara' X". Il contratto marca
 * l'anteprima come **indicativa** e non riserva nulla: un numero mostrato come
 * certo sarebbe falso al secondo avvio concorrente.
 */

import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline, SettingsOutlined, TuneOutlined } from "@mui/icons-material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { create_UUID, getTranslation } from "common";
import { useSnackbar, useTranslations } from "hooks";
import type { RegistryRead, RegistryScope } from "model/CustomFields";
import { apiError, customFieldsApi } from "api/customFieldsApi";
import DialogHeader from "components/dialogs/DialogHeader";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import RegistryEditorDialog from "./RegistryEditorDialog";
import SequenceActionsDialog from "./SequenceActionsDialog";
import { messageKeyFor } from "./errorMessages";
import { registriesOfScope } from "./registryDraft";
import { currentValue } from "./sequences";

interface OrganizationSequencesPanelProps {
  /**
   * L'identità dei progressivi che questo pannello amministra: l'organizzazione
   * nelle sue impostazioni. Le impostazioni di un'azienda non sono il posto in
   * cui guardare i contatori locali di un modello.
   */
  identity: RegistryScope;
}

const OrganizationSequencesPanel: React.FC<OrganizationSequencesPanelProps> = ({
  identity,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  /**
   * **Tutti** i progressivi dell'organizzazione, non solo quelli di questo
   * ambito: la vista di un modello ne mostra due elenchi — i progressivi
   * condivisi, in sola lettura, e i suoi — e filtrarli qui ne perderebbe metà.
   */
  const [registries, setRegistries] = useState<RegistryRead[]>([]);
  const [loading, setLoading] = useState(false);
  /**
   * L'ultimo errore, **intero**: non la sua stringa.
   *
   * Serve il codice del contratto per distinguere un servizio spento
   * (`CUSTOM_FIELDS_UNAVAILABLE`, che è una configurazione) da un guasto
   * qualsiasi: la frase da mostrare è diversa, e "riprova più tardi" su un
   * servizio non attivato è un consiglio che non porta da nessuna parte.
   */
  const [error, setError] = useState<unknown>(null);
  /** Il registro su cui si stanno facendo azioni, o `null`. */
  const [selected, setSelected] = useState<RegistryRead | null>(null);
  /** Il registro in configurazione, `null` quando non se ne sta configurando, */
  /** `"new"` quando se ne sta creando uno. */
  const [editing, setEditing] = useState<RegistryRead | "new" | null>(null);
  /** Il registro di cui si sta confermando l'archiviazione, o `null`. */
  const [archiving, setArchiving] = useState<RegistryRead | null>(null);
  /** Il registro la cui archiviazione è in corso. */
  const [archivePending, setArchivePending] = useState(false);

  const handleArchive = async () => {
    if (!archiving) return;
    setArchivePending(true);
    try {
      await customFieldsApi.archiveRegistry(identity.organizationId, archiving.registryId, {
        operationId: create_UUID(),
        expectedRevision: archiving.revision,
      });
      setArchiving(null);
      // Il registro esce dall'elenco: si rilegge invece di toglierlo a mano.
      void carica();
    } catch (e) {
      // La snackbar, non un'alert nel dialogo: si chiude comunque. Un
      // `REGISTRY_IN_USE` (blocco sopraggiunto fra lettura e click) cade qui
      // come qualsiasi altro rifiuto, senza un ramo speciale.
      sendSnack({
        message: getTranslation(translations, messageKeyFor(apiError(e))),
        type: "error",
      });
      setArchiving(null);
    } finally {
      setArchivePending(false);
    }
  };


  const carica = useCallback(async () => {
    if (!identity.organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const page = await customFieldsApi.getRegistries(identity.organizationId);
      // L'elenco intero: il filtro per ambito sta nei due `useMemo` qui sotto,
      // perche' questa lettura serve entrambe le sezioni della vista di modello.
      setRegistries(page.items ?? []);
    } catch (e) {
      // Un elenco vuoto per un errore di rete sarebbe indistinguibile da "non
      // ci sono registri": si dice che e' fallito.
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [identity.organizationId, identity.scope, identity.modelId]);

  useEffect(() => {
    void carica();
  }, [carica]);

  /**
   * I progressivi condivisi da tutta l'organizzazione: nella vista di un
   * modello si leggono soltanto, nella vista dell'azienda sono l'elenco da
   * amministrare.
   */
  const dellOrganizzazione = useMemo(
    () =>
      registriesOfScope(registries, {
        organizationId: identity.organizationId,
        scope: "organization",
      }),
    [registries, identity.organizationId]
  );

  if (loading) {
    return (
      <Stack alignItems="center" py={2}>
        <CircularProgress size={22} data-testid="cf-sequences-loading" />
      </Stack>
    );
  }

  if (error) {
    // Retry an unavailable service without losing the current screen.
    const codice = apiError(error)?.code;
    return (
      <Alert
        severity="error"
        data-testid="cf-sequences-error"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => void carica()}
            data-testid="cf-sequences-retry"
          >
            {getTranslation(translations, "custom_fields.action.retry")}
          </Button>
        }
      >
        {getTranslation(
          translations,
          codice === "CUSTOM_FIELDS_UNAVAILABLE"
            ? "custom_fields.error.unavailable"
            : "custom_fields.sequence.load_failed"
        )}
      </Alert>
    );
  }

  /** L'etichetta del valore a cui sta il contatore. */
  const currentLabel = getTranslation(translations, "custom_fields.sequence.current");

  /**
   * Una riga dell'elenco: nome, valore attuale, chip e i tre comandi (azioni,
   * configura, cestino). Il pannello amministra i progressivi
   * dell'organizzazione, ed e' il posto che li governa.
   */
  const rigaProgressivo = (registry: RegistryRead) => (
    <Stack
      key={registry.registryId}
      direction="row"
      spacing={1}
      alignItems="center"
      data-testid={`cf-sequence-${registry.registryId}`}
    >
      <Typography variant="body2" sx={{ flex: 1 }}>
        {registry.name} ({registry.code})
      </Typography>

      {/* Il valore a cui sta **adesso** il contatore, riga per riga: è la
          domanda che si fa guardando un contatore, e finora la si poteva
          leggere solo aprendo le azioni di quel registro. Non è `lastIssued`
          (l'ultimo emesso, che un azzeramento non tocca) ma il cursore, cioè
          il prossimo meno uno: azzerando torna a zero. Il trattino quando non
          si sa tiene le righe della stessa larghezza. */}
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid={`cf-sequence-current-${registry.registryId}`}
      >
        {currentLabel}
        {": "}
        {currentValue(registry.nextValue) ?? "—"}
      </Typography>

      {/* Il registro locale e quello d'organizzazione si mostrano distinti:
          emettono dallo stesso contatore condiviso solo nel secondo caso, e
          confonderli farebbe credere che due campi non si rubino i numeri a
          vicenda. */}
      <Chip
        size="small"
        variant="outlined"
        label={getTranslation(
          translations,
          `custom_fields.sequence.scope.${registry.scope}`
        )}
      />

      {/* Un registro disattivato non emette: va visto subito, non scoperto al
          primo avvio che non produce un codice. */}
      {!registry.active && (
        <Chip
          size="small"
          color="warning"
          label={getTranslation(translations, "custom_fields.sequence.inactive")}
        />
      )}

      <Tooltip
        title={getTranslation(translations, "custom_fields.sequence.actions")}
      >
        <IconButton
          size="small"
          data-testid={`cf-sequence-actions-${registry.registryId}`}
          onClick={() => setSelected(registry)}
        >
          <TuneOutlined fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Configurare è un'azione diversa da azzerare o spostare il cursore:
          una cambia il registro, l'altra i suoi numeri. Come la delete:
          cambiare la configurazione mentre ci sono istanze attive
          cambierebbe i numeri che quelle preleveranno, e il vincolo lo
          dichiara il server in `archiveBlocked`. */}
      <Tooltip
        title={
          Boolean(registry.archiveBlocked)
            ? getTranslation(translations, "custom_fields.sequence.blocked_active_process")
            : getTranslation(translations, "custom_fields.sequence.edit")
        }
      >
        <span>
          <IconButton
            size="small"
            disabled={Boolean(registry.archiveBlocked)}
            data-testid={`cf-registry-edit-${registry.registryId}`}
            onClick={() => setEditing(registry)}
          >
            <SettingsOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* Archiviare è un soft delete: il progressivo esce dall'elenco, non
          dai codici già emessi. Il vincolo dipende dai modelli che usano
          il registro (istanze di processo attive) e lo sa solo il server,
          che lo dichiara in `archiveBlocked`; il perché lo dice il tooltip.
          `Boolean()` regge una risposta che non lo porta. */}
      <Tooltip
        title={
          Boolean(registry.archiveBlocked)
            ? getTranslation(translations, "custom_fields.sequence.blocked_active_process")
            : ""
        }
      >
        <span>
          <IconButton
            size="small"
            disabled={Boolean(registry.archiveBlocked)}
            data-testid={`cf-registry-delete-${registry.registryId}`}
            onClick={() => setArchiving(registry)}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  /** Il "nuovo": crea un progressivo dell'organizzazione. */
  const nuovoProgressivo = (
    <FlowentiButton
      size="small"
      variant="outlined"
      startIcon={<Add fontSize="small" />}
      onClick={() => setEditing("new")}
      data-testid="cf-registry-new"
    >
      {getTranslation(translations, "custom_fields.sequence.new")}
    </FlowentiButton>
  );

  return (
    <Stack gap={2} data-testid="cf-sequences-panel">
      {/* L'elenco unico dei progressivi dell'azienda. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">
          {getTranslation(translations, "custom_fields.sequence.organization")}
        </Typography>
        {nuovoProgressivo}
      </Stack>

      {dellOrganizzazione.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="cf-sequences-empty"
        >
          {getTranslation(translations, "custom_fields.sequence.none_available")}
        </Typography>
      ) : (
        <Stack gap={1}>
          {dellOrganizzazione.map((registry) => rigaProgressivo(registry))}
        </Stack>
      )}

      <SequenceActionsDialog
        open={selected !== null}
        registry={selected}
        organizationId={identity.organizationId}
        onClose={() => setSelected(null)}
        onDone={() => {
          // Dopo un'azione riuscita il registro e' cambiato: si rilegge invece
          // di aggiornarlo a mano, perche' il cursore lo decide il server.
          setSelected(null);
          void carica();
        }}
      />

      <RegistryEditorDialog
        open={editing !== null}
        identity={identity}
        registry={editing === "new" ? null : editing}
        onClose={(changed) => {
          setEditing(null);
          // Si rilegge solo se qualcosa è cambiato: rileggere sempre farebbe
          // lampeggiare l'elenco anche quando non è successo niente.
          if (changed) void carica();
        }}
      />

      {/* Conferma breve: archiviare non distrugge i codici già emessi, ma il
          registro sparisce dall'elenco, e una cosa che sparisce si conferma. */}
      <Dialog
        open={archiving !== null}
        onClose={() => setArchiving(null)}
        maxWidth="sm"
        fullWidth
        data-testid="cf-registry-delete-dialog"
      >
        <DialogHeader sx={{ px: 4 }} onClose={() => setArchiving(null)}>
          {getTranslation(translations, "custom_fields.sequence.delete")}
        </DialogHeader>
        <DialogContent>
          <Typography variant="body2" data-testid="cf-registry-delete-confirm-text">
            {getTranslation(translations, "custom_fields.sequence.delete_confirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiving(null)} data-testid="cf-registry-delete-cancel">
            {getTranslation(translations, "custom_fields.action.cancel")}
          </Button>
          <Button
            color="error"
            disabled={archivePending}
            onClick={() => void handleArchive()}
            data-testid="cf-registry-delete-confirm"
          >
            {getTranslation(translations, "custom_fields.sequence.delete_confirm_action")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default OrganizationSequencesPanel;
